import React, { useMemo, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";

export type DropdownItem = {
  id: string | number;
  name?: string;
  label?: React.ReactNode;
  href?: string;
  params?: Record<string, string>;
  onClick?: string | ((item: DropdownItem, event?: React.MouseEvent) => void);
  isLabel?: boolean;
  isSeparator?: boolean;
  color?: string;
  icon?: React.ReactNode;
  parent?: { name: string; id: string | number; type?: string; href?: string };
  disabled?: boolean;
};

interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  align?: "start" | "end" | "center";
  side?: "top" | "bottom" | "left" | "right";
  onClick?: (
    item: DropdownItem,
    functionType: string,
    parent?: { name: string; id: string | number; type?: string; href?: string }
  ) => void;
}

export function Dropdown({ trigger, items, align = "start", side = "bottom", onClick }: DropdownProps) {
  const [open, setOpen] = useState(false);

  // Compute items with icons, avoid mutating props
  const computedItems = useMemo(
    () =>
      items.map((item) =>
        item.onClick === "delete" ? { ...item, icon: <Trash2 className="w-4 h-4 text-red-500" /> } : item
      ),
    [items]
  );

  const handleItemClick = (item: DropdownItem) => (e: React.MouseEvent | React.KeyboardEvent) => {
    if (item.disabled) return;
    setOpen(false);

    if (item.onClick) {
      e.preventDefault();
      e.stopPropagation();

      if (typeof item.onClick === "function") {
        item.onClick(item);
      } else {
        onClick?.(item, item.onClick, item.parent);
      }
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} className="p-0">
        {computedItems.map((item) => {
          if (item.isLabel) {
            return (
              <DropdownMenuLabel key={item.id} className="p-0 font-normal">
                {item.label}
              </DropdownMenuLabel>
            );
          }

          if (item.isSeparator) {
            return <DropdownMenuSeparator key={item.id} />;
          }

          const itemClass = `flex items-center justify-between gap-2 px-3 py-2 w-full ${
            item.disabled ? "opacity-50" : ""
          }`;

          return (
            <DropdownMenuItem
              key={item.id}
              className={`p-0 ${item.color ? `text-${item.color}` : ""}`}
              aria-disabled={item.disabled}
            >
              {item.href ? (
                <Link
                  to={item.href}
                  params={item.params || {}}
                  className={itemClass}
                  onClick={handleItemClick(item)}
                  tabIndex={item.disabled ? -1 : 0}
                  aria-disabled={item.disabled}
                >
                  {item.label || item.name}
                  {item.icon}
                </Link>
              ) : (
                <div
                  className={itemClass}
                  onClick={handleItemClick(item)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !item.disabled) {
                      handleItemClick(item)(e);
                    }
                  }}
                  role="button"
                  tabIndex={item.disabled ? -1 : 0}
                  aria-disabled={item.disabled}
                >
                  {item.label || item.name}
                  {item.icon}
                </div>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
